import React from 'react';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';
import { filter, isNil, round } from 'lodash';
import { Popover } from 'antd';

import { useMeasurement } from '@/hooks/useMeasurement';
import { useValueByConnectivity } from '@/hooks/useValueByConnectivity';
import { useStores } from '@/hooks/use-stores';
import NodeWidget from '@/comp/MapBuilder/nodes/nodeWidget/NodeWidget';
import PackageCareIndication from '@/comp/MapBuilder/nodes/nodeWidget/DetailsDrawer/PackageCareIndication/PackageCareIndication';
import { DeviceType } from '@/constants/deviceTypeEnum';
import { AlertStatus, CompressorActivityStateEnum } from '@/constants/enums';
import DetailsWidgetSlider from '@/comp/StationControlCenter/ControlCenterCompressorWidget/DetailsWidgetSlider/DetailsWidgetSlider';
import { SliderPoint } from '@/comp/StationControlCenter/ControlCenterCompressorWidget/DetailsWidgetSlider/DetailsWidgetSlider.types';
import Super from '@/comp/Super';
import SuperUnit from '@/comp/SuperUnit';
import {
  useCentrifugalTargetSetpointValue,
  getFirstFlowSensor,
  getFirstPowerSensor,
  useFlowValue,
  useMaxSetpointTitle,
  useMinSetpointValue,
  getPowerSensorKw,
  useGetCompressorSliderPoints,
  useMaxSetpointValue,
  getPowerValue,
} from '@/comp/MapBuilder/nodes/compressor/CompressorNodeWidget.utils';
import GetConnectivityIconAndStatus from '@/comp/MapBuilder/nodes/compressor/GetConnectivityIconAndStatus/GetConnectivityIconAndStatus';
import ConnectivityIcon from '@/comp/MapBuilder/components/ConnectivityIcon/ConnectivityIcon';
import { usePowerOnOffVisibility } from '@/comp/MapBuilder/nodes/nodeWidget/DetailsDrawer/Footer/StartStopButtons/StartStopButtons.utils';
import CompressorIcon from '@/comp/common/icons/CompressorIcon';

import { MaintenanceSwitch } from '../ToggleSwitches/MaintenanceSwitch';
import { getPoweredOnState } from '../SccUtils';
import { ModalWrapper, Container, OperationParametersRow } from './ControlCenterCompressorWidget.styles';
import Alerts from './Alerts';
import {
  getFlowTooltip,
  getInverterSpeedTooltipContent,
  getInverterSpeedValue,
} from './ControlCenterCompressorWidget.utils';
import StartStopButtons from '@/comp/MapBuilder/nodes/nodeWidget/DetailsDrawer/Footer/StartStopButtons/StartStopButtons';

const CompressorControl = observer(({ compressor }) => {
  const { t } = useTranslation();
  const { tunit, tvalue } = useMeasurement();
  const { tcunit, tcvalue } = useValueByConnectivity();
  const { stationStore, alertStore } = useStores();
  const alertsByDeviceId = alertStore.filterAlertsByDeviceId(compressor.deviceId);
  const activeAlerts = alertStore.filterActiveAlerts(alertsByDeviceId);
  const activeOpenAlerts = filter(activeAlerts, { status: AlertStatus.Open }) || [];
  const onDevices = stationStore.currentStation?.devices?.filter(device =>
    compressor.onDevices.includes(device.deviceId),
  );
  const poweredOn = getPoweredOnState(compressor.poweredOn);
  const { poweredOnMetricPresent, powerChangeSupportedAction } = usePowerOnOffVisibility(compressor.deviceId);
  const shouldDisplayPoweredOnIndication = poweredOnMetricPresent && powerChangeSupportedAction;

  const firstPowerSensor = getFirstPowerSensor(compressor, onDevices);
  const firstPowerSensorKw = getPowerSensorKw(firstPowerSensor);
  const powerKw = getPowerValue(compressor, stationStore.currentStation?.id, firstPowerSensor?.powerKw, tvalue);
  const firstFlowSensor = getFirstFlowSensor(compressor, onDevices);
  const currentAsset = stationStore.assetsDevices?.filter(device => device.deviceId === compressor.deviceId)[0];
  const isCentrifugal = [DeviceType.Centrifugal, DeviceType.CentrifugalAndPowerSensor].includes(compressor.deviceType);
  const PowerValue = () => {
    const powerDisplayValue = tcvalue(isNil(powerKw) ? t('N/A') : round(powerKw), compressor.connectivity);
    const powerDisplayUnit = tcunit('kW', powerKw, compressor.connectivity);
    if (powerDisplayValue === t('N/A') || powerDisplayValue === t('_ _')) return null;

    return (
      <div className="metric-container">
        <div className="power-text">{t('Power')}</div>
        <div className="power-value-box">
          <span className="power-value">{powerDisplayValue}</span>
          {powerDisplayUnit && <span className="power-unit">{powerDisplayUnit}</span>}
        </div>
      </div>
    );
  };

  const FlowValue = () => {
    const flowValue = useFlowValue(compressor, firstFlowSensor, firstPowerSensorKw);
    if (isNil(flowValue)) return null;

    const flowDisplayValue = tcvalue(flowValue, compressor.connectivity);
    const flowDisplayUnit = tcunit(<SuperUnit unit="m3/min" />, flowValue, compressor.connectivity);
    if (flowDisplayValue === t('N/A') || flowDisplayValue === t('_ _')) return null;

    return (
      <Popover placement={'bottomLeft'} content={getFlowTooltip(compressor, t)}>
        <div className="metric-container">
          <div className="power-text">
            <Trans i18nKey={t('Flow')}>
              <Super />
            </Trans>
          </div>
          <div className="power-value-box">
            <span className="power-value">{flowDisplayValue}</span>
            {flowDisplayUnit && <span className="power-unit">{flowDisplayUnit}</span>}
          </div>
        </div>
      </Popover>
    );
  };

  const RPMValue = () => {
    switch (compressor.deviceType) {
      case DeviceType.VariableSpeedDrive:
      case DeviceType.VariableSpeedDriveAndPowerSensor: {
        const inverterSpeedValue = getInverterSpeedValue(compressor, tvalue);
        if (isNil(inverterSpeedValue)) return null;

        const rpmDisplayValue = tcvalue(inverterSpeedValue, compressor.connectivity);
        if (rpmDisplayValue === t('N/A') || rpmDisplayValue === t('_ _')) return null;

        return (
          <Popover placement={'bottomLeft'} content={getInverterSpeedTooltipContent(compressor, tvalue, t)}>
            <div className="metric-container">
              <div className="rpm-text">{t('InvertedSpeed')}</div>
              <div className="rpm-value-box">
                <span className="rpm-value">{rpmDisplayValue}</span>
                <span className="rpm-unit">{tcunit('%', inverterSpeedValue, compressor.connectivity)}</span>
              </div>
            </div>
          </Popover>
        );
      }
      default:
        return null;
    }
  };

  const hasInletValve = !isNaN(compressor.inletValveOpening) && compressor.inletValveOpening != null;
  const hasBypassValve = !isNaN(compressor.bypassValveOpening) && compressor.bypassValveOpening != null;

  const connectivityIconAndStatus = GetConnectivityIconAndStatus({
    connectivity: compressor.connectivity,
    activity: compressor.activity,
    extendedActivity: compressor.extendedActivity,
    controlMode: compressor.controlMode,
    isSelectedOnMap: false,
    size: '27px',
  });

  const mainSliderPoints: SliderPoint[] = [
    { value: useMinSetpointValue(compressor), label: t('LOAD') },
    { value: useMaxSetpointValue(compressor), label: useMaxSetpointTitle(compressor) },
  ];

  if ([DeviceType.Centrifugal, DeviceType.CentrifugalAndPowerSensor].includes(compressor.deviceType)) {
    mainSliderPoints.push({
      value: useCentrifugalTargetSetpointValue(compressor),
      label: t('Target'),
      highlighted: true,
    });
  }

  const OperationParameterBox = (title: string, value: number, unit: string) => {
    return (
      <div className="operation-parameter">
        <div className="operation-parameter-title">{t(title)}</div>
        <div className="operation-parameter-value">
          <div className="value">{Number(value?.toFixed(0))}</div>
          <div className="unit">{unit}</div>
        </div>
      </div>
    );
  };

  return (
    <ModalWrapper>
      <NodeWidget entity={compressor} onDevices={onDevices} fromSCC>
        <Container $isInMaintenance={compressor.inMaintenance} $isPoweredOn={poweredOn}>
          <div className="top-row top-padding">
            <div className="compressor-type-container">
              <CompressorIcon width={22} height={22} />
              <div className="compressor-type-label">Comp</div>
            </div>
            <div className="compressor-title">
              <div className="name ellipsis">{compressor.name ?? ''}</div>
              <div className="model ellipsis">{compressor.model ?? compressor.modelDisplayName ?? ''}</div>
            </div>
            <div className="connectivity-icon-wrapper">
              <PackageCareIndication entity={compressor} mapView width={65} height={32} />
              <div className="icons-row">
                <ConnectivityIcon
                  controlMode={compressor.controlMode}
                  isCompressor={true}
                  connectivity={compressor.connectivity}
                  helixConnectivityStatus={currentAsset?.helixConnectivityStatusC}
                  messages={compressor.messages}
                  width={24}
                  height={24}
                />
                {connectivityIconAndStatus.icon}
              </div>
              <div className="status-name">{connectivityIconAndStatus.status}</div>
            </div>
          </div>
          <div className="horizontal-border" />

          <div className="middle-row-container">
            <div className="middle-row">
              <DetailsWidgetSlider
                title={null}
                value={tvalue(Number(compressor.pressureBar), 'bar')}
                unit={t(tunit('Bar'))}
                min={useMinSetpointValue(compressor)}
                max={useMaxSetpointValue(compressor)}
                points={useGetCompressorSliderPoints(compressor, stationStore.hasNewActionsFeature)}
                connectivity={compressor.connectivity}
                highlightSlider={compressor.activity?.toLowerCase() === CompressorActivityStateEnum.Load}
                addSideGaps={true}
                isInMaintenance={compressor.inMaintenance ?? false}
                hasTarget
              />
            </div>

            <OperationParametersRow $hasInletValve={hasInletValve} $hasBypassValve={hasBypassValve}>
              <div className="alerts-container">
                <Alerts activeOpenAlerts={activeOpenAlerts} />
              </div>
              <div className="valves-container">
                {isCentrifugal && (
                  <>
                    {hasInletValve && OperationParameterBox('INLET_VALVE', compressor.inletValveOpening, '%')}
                    {hasBypassValve && OperationParameterBox('BYPASS_VALVE', compressor.bypassValveOpening, '%')}
                  </>
                )}
              </div>
            </OperationParametersRow>
          </div>

          <div className="horizontal-border" />

          <div className="bottom-row bottom-padding">
            <div className="bottom-left-container">
              <div className="flex-row-centered">
                <PowerValue />
                <RPMValue />
                <FlowValue />
              </div>
            </div>
            <div className="vertical-border" />
            {shouldDisplayPoweredOnIndication && (
              <>
                <div className="start-stop-buttons-container">
                  <StartStopButtons
                    deviceId={compressor.deviceId}
                    deviceType={compressor.deviceType}
                    poweredOn={compressor.poweredOn}
                    hideLabels={true}
                  />
                </div>
              </>
            )}
            {!isNil(compressor.inMaintenance) && (
              <>
                <div className="toggle-status-box">
                  <MaintenanceSwitch inMaintenance={compressor.inMaintenance} deviceId={compressor.deviceId} />
                </div>
              </>
            )}
          </div>
        </Container>
      </NodeWidget>
    </ModalWrapper>
  );
});

export default CompressorControl;
